/**
 * Integration Service Registry
 * 
 * This module provides a registry pattern for managing integration services.
 * It allows easy registration of new integration services and retrieval
 * of the appropriate service based on the integration type.
 * 
 * To add a new integration:
 * 1. Create a service that implements IntegrationService interface
 * 2. Register it using registerIntegrationService()
 */

import type { IntegrationService, SupportedIntegrationType } from './integration-service.interface';
import { financialsService } from './financials-graphql-service';
import { dimensionsService } from './dimensions-graphql-service';

/**
 * Registry mapping integration types to their service implementations
 */
const integrationServiceRegistry = new Map<SupportedIntegrationType, IntegrationService>();

/**
 * Register an integration service for a specific integration type
 * @param integrationType - The integration type identifier
 * @param service - The service implementation
 */
export function registerIntegrationService(
  integrationType: SupportedIntegrationType,
  service: IntegrationService
): void {
  integrationServiceRegistry.set(integrationType, service);
}

/**
 * Get the integration service for a specific integration type
 * Falls back to financialsService if the type is not registered
 * @param integrationType - The integration type identifier
 * @returns The integration service for the specified type
 */
export function getIntegrationService(integrationType: SupportedIntegrationType | null): IntegrationService {
  if (!integrationType) {
    return financialsService;
  }

  const service = integrationServiceRegistry.get(integrationType);
  if (service) {
    return service;
  }

  // Fallback to financials service for unknown types
  console.warn(`Unknown integration type: ${integrationType}, falling back to financials service`);
  return financialsService;
}

/**
 * Check if an integration type has a registered service
 * @param integrationType - The integration type to check
 * @returns True if the integration type has a registered service
 */
export function hasIntegrationService(integrationType: SupportedIntegrationType): boolean {
  return integrationServiceRegistry.has(integrationType);
}

/**
 * Get all registered integration types
 * @returns Array of registered integration type identifiers
 */
export function getRegisteredIntegrationTypes(): SupportedIntegrationType[] {
  return Array.from(integrationServiceRegistry.keys());
}

/**
 * Clear all registered services (useful for testing)
 */
export function clearIntegrationServiceRegistry(): void {
  integrationServiceRegistry.clear();
}

/**
 * Initialize the registry with default services
 * This is called automatically when the module is imported
 */
function initializeDefaultServices(): void {
  registerIntegrationService('access-financials', financialsService);
  registerIntegrationService('access-dimensions', dimensionsService);
  // Future integrations can be registered here:
  // registerIntegrationService('xero', xeroService);
  // registerIntegrationService('quickbooks', quickbooksService);
}

// Initialize default services on module load
initializeDefaultServices();

/**
 * Re-initialize the registry with default services
 * Useful for testing or resetting the registry state
 */
export function reinitializeDefaultServices(): void {
  clearIntegrationServiceRegistry();
  initializeDefaultServices();
}
